import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useI18n } from '../../../contexts/I18nContext';
import { HardwareType, ModelReference, typesService } from '../../../services/typesService';
import { clientService } from '../../../services/clientService';
import { Client } from '../../../types/client';
import { SortDirection, nextSort } from '../../common/SortableHeader';

export interface ModelRefWithHardware extends ModelReference {
  hardware_type_code: string;
  hardware_type_name: string;
}

export type RefSortField = 'code' | 'name' | 'hardware_type_code' | 'updated_at';

export const GLOBAL_CLIENT = '00000000-0000-0000-0000-000000000000';

/**
 * Every model reference of the catalogue, with the hardware type it
 * revises: one request (the types with their references embedded), then
 * searched, filtered and sorted here.
 */
export const useModelReferencesList = (refreshTrigger?: number) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { t } = useI18n();

  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<ModelRefWithHardware[]>([]);
  const [clientList, setClientList] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterHardwareType, setFilterHardwareType] = useState<number | 'all'>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState<{ field: RefSortField; direction: SortDirection }>({ field: 'hardware_type_code', direction: 'asc' });

  const includeInactive = showInactive && canSeeInactive;

  const fetchAll = useCallback(async (isMounted: { current: boolean }) => {
    if (!isAuthenticated) {
      if (isMounted.current) setLoading(false);
      return;
    }
    try {
      if (isMounted.current) {
        setLoading(true);
        setError(null);
      }
      const perType = await typesService.getAllModelReferences(includeInactive);
      if (!isMounted.current) return;
      setHardwareTypes(perType.map(({ type }) => type));
      setModelRefs(perType.flatMap(({ type, refs }) => refs.map((ref) => ({ ...ref, hardware_type_code: type.code, hardware_type_name: type.name }))));
    } catch (err: unknown) {
      console.error('[MODEL REFERENCES TABLE] Error fetching data:', err);
      if (isMounted.current) setError(err instanceof Error ? err.message : t('deviceTypes.errorLoadingModelRefs'));
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [isAuthenticated, includeInactive, t]);

  useEffect(() => {
    const isMounted = { current: true };
    fetchAll(isMounted);
    return () => { isMounted.current = false; };
  }, [fetchAll, refreshTrigger]);

  const reload = useCallback(() => { fetchAll({ current: true }); }, [fetchAll]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    clientService.fetchAllClients()
      .then((clients) => { if (!cancelled) setClientList(clients); })
      .catch((err) => { if (!cancelled) console.error('[MODEL REFERENCES TABLE] Error fetching clients:', err); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const clientName = useCallback((clientId: string): string => {
    if (clientId === GLOBAL_CLIENT) return t('deviceTypes.modelRef.global');
    return clientList.find((c) => c.id === clientId)?.name || t('deviceTypes.modelRef.unknownClient');
  }, [clientList, t]);

  const rows = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    const filtered = modelRefs.filter((ref) => {
      const matchesSearch = ref.code.toLowerCase().includes(needle)
        || (ref.name?.toLowerCase().includes(needle) ?? false)
        || ref.hardware_type_code.toLowerCase().includes(needle)
        || ref.hardware_type_name.toLowerCase().includes(needle);
      const matchesType = filterHardwareType === 'all' || ref.hardware_type_id === filterHardwareType;
      return matchesSearch && matchesType;
    });
    const dir = sort.direction === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => (String(a[sort.field] ?? '').localeCompare(String(b[sort.field] ?? '')) || a.code.localeCompare(b.code)) * dir);
  }, [modelRefs, searchTerm, filterHardwareType, sort]);

  const facts = useMemo(() => ({
    total: modelRefs.length,
    active: modelRefs.filter((r) => r.is_active).length,
    inactive: modelRefs.filter((r) => !r.is_active).length,
    typesWithRefs: new Set(modelRefs.map((r) => r.hardware_type_id)).size,
  }), [modelRefs]);

  const hasFilters = Boolean(searchTerm || filterHardwareType !== 'all');

  return {
    canSeeInactive, loading, error, hardwareTypes, rows, facts, clientName, reload,
    searchTerm, setSearchTerm, filterHardwareType, setFilterHardwareType, showInactive, setShowInactive, hasFilters,
    sort, sortBy: (field: RefSortField) => setSort((s) => nextSort(s, field, ['updated_at'])),
  };
};

export type ModelReferencesListState = ReturnType<typeof useModelReferencesList>;
